import { useState, useEffect, useRef, useCallback } from 'react';
import Fuse from 'fuse.js';
import staticSearchIndex from '../data/searchIndex';

const PY_BASE = (process.env.REACT_APP_API_PY_BASE_URL || 'https://api-py.mahabharatanubhavkendra.com/').replace(/\/+$/, '');

const fuseOptions = {
  keys: [
    { name: 'title', weight: 0.6 },
    { name: 'excerpt', weight: 0.3 },
    { name: 'page', weight: 0.1 },
  ],
  threshold: 0.35,
  includeScore: true,
  minMatchCharLength: 2,
};

// Fetch live events and attractions once and cache them
let liveCache = null;
let liveFetchPromise = null;

async function fetchLiveItems() {
  if (liveCache) return liveCache;
  if (liveFetchPromise) return liveFetchPromise;

  liveFetchPromise = (async () => {
    const results = [];
    try {
      const eventsRes = await fetch(`${PY_BASE}/api/shows/?active=true`);
      if (eventsRes.ok) {
        const data = await eventsRes.json();
        const events = Array.isArray(data) ? data : (data.results || []);
        events.forEach((ev) => {
          results.push({
            title: ev.name || ev.title || 'Event',
            excerpt: [ev.location, ev.show_time, ev.price_per_person ? `₹${ev.price_per_person}/person` : ''].filter(Boolean).join(' · '),
            page: 'Events',
            path: '/events',
            anchor: '',
          });
        });
      }
    } catch {
      // graceful degrade
    }
    try {
      const blocksRes = await fetch(`${PY_BASE}/api/attraction-blocks/`);
      if (blocksRes.ok) {
        const data = await blocksRes.json();
        const blocks = Array.isArray(data) ? data : (data.results || []);
        blocks.forEach((block) => {
          results.push({
            title: block.name || block.title || 'Attraction Block',
            excerpt: block.description || block.short_description || '',
            page: 'Main Attractions',
            path: '/main-attractions',
            anchor: '',
          });
        });
      }
    } catch {
      // graceful degrade
    }
    liveCache = results;
    return results;
  })();

  return liveFetchPromise;
}

export function useSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimer = useRef(null);
  const fuseRef = useRef(null);

  // Build Fuse index from static + live items
  const buildIndex = useCallback(async (staticItems, liveItems) => {
    const allItems = [...staticItems, ...liveItems];
    fuseRef.current = new Fuse(allItems, fuseOptions);
  }, []);

  useEffect(() => {
    fetchLiveItems().then((liveItems) => {
      buildIndex(staticSearchIndex, liveItems);
    });
  }, [buildIndex]);

  const search = useCallback((q) => {
    setQuery(q);
    clearTimeout(debounceTimer.current);

    if (!q.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      setIsSearching(true);
      // Ensure live items are loaded before searching
      if (!fuseRef.current) {
        const liveItems = await fetchLiveItems();
        await buildIndex(staticSearchIndex, liveItems);
      }
      const fuseResults = fuseRef.current ? fuseRef.current.search(q) : [];
      setResults(fuseResults.slice(0, 8).map((r) => r.item));
      setIsSearching(false);
    }, 200);
  }, [buildIndex]);

  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsSearching(false);
    clearTimeout(debounceTimer.current);
  }, []);

  return { query, results, isSearching, search, clear };
}
